import { useAppSystem } from "@/global/hooks/useAppSystem";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { useToggle } from "@/shared/base/hooks/useToggle";
import { useCartHelpers } from "../useCartHelpers";
import { OutlinedIconButton } from "@/shared/base/components/OutlinedIconButton/OutlinedIconButton";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { LoadingButton } from "@mui/lab";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useAppStore } from "@/global/store";
import { selectEventsSelected } from "@/global/store/data/purchaseSeats/events";
import {
  selectCartManager,
  selectProducts,
} from "@/global/store/data/purchaseSeats/cartManager";
import { selectIsAda, selectSeatCount } from "@/global/store/data/gameFilters";
import { validateInventorySelection } from "../ChooseSeats/utils.chooseSeats";
import React from "react";
import { CartBackButtonWarningModal } from "../modals/CartBackButtonWarningModal";
import { useCartNavigationHelper } from "../useCartBackButtonHelper";
import { useRepsProduct } from "../useRepsProduct";
import { useSelectActiveEvent } from "../ChooseSeats/useSelectActiveEvent";
import { findUnholdEventsInCart } from "../utils.cart";

export function CartBackButton() {
  const { sm: smDown } = useCssMediaQuery("down");
  const modalProps = useToggle();
  const { backToFindGames } = useCartNavigationHelper();

  const handleBackToFindGames = () => {
    backToFindGames();
    modalProps.toggle();
  };

  return (
    <>
      <CartBackButtonWarningModal
        modalProps={modalProps}
        onClickBack={handleBackToFindGames}
      />
      <OutlinedIconButton
        size={smDown ? "small" : "medium"}
        onClick={() => modalProps.toggle()}
      >
        <ArrowBackRoundedIcon />
      </OutlinedIconButton>
    </>
  );
}

export function CartNextButton() {
  const { teamConfig } = useAppSystem();
  const { inventoryType, purchaseSeatConfigs } = teamConfig;
  const { isSimilarSeatForAllEvents } = purchaseSeatConfigs;
  const { sm: smDown } = useCssMediaQuery("down");
  const { handleManageCart, handleAutoSeatSelection } = useCartHelpers();

  // states
  const [isLoading, setIsLoading] = React.useState(false);
  const selectedEvents = useAppStore(selectEventsSelected);
  const { activeEvent } = useSelectActiveEvent();
  const cartState = useAppStore(selectCartManager);
  const cartProducts = useAppStore(selectProducts(activeEvent?.eventCode));
  const isAda = useAppStore(selectIsAda);
  const seatCount = useAppStore(selectSeatCount);
  const unHoldEvents = findUnholdEventsInCart(selectedEvents, cartState);

  const { states: { isRepProduct } } = useRepsProduct(); // prettier-ignore

  // validators
  const { isEventValidated } = validateInventorySelection(cartProducts, {
    seatCount,
    inventoryType: isAda ? "ADA" : inventoryType,
  });

  const handleClick = async () => {
    setIsLoading(true);
    const moreThanTwoSelectedEvents = selectedEvents.length <= 2;
    if (isRepProduct && moreThanTwoSelectedEvents) {
      await handleManageCart();
      setIsLoading(false);
      return;
    }

    if (isSimilarSeatForAllEvents && !isAda) {
      await handleAutoSeatSelection();
    } else {
      await handleManageCart();
    }
    setIsLoading(false);
  };

  return (
    <LoadingButton
      loading={isLoading}
      onClick={handleClick}
      size={"large"}
      disabled={!isEventValidated || isLoading}
      variant={"contained"}
      endIcon={<ArrowForwardRoundedIcon />}
      sx={{ width: smDown ? "fit-content" : "180px", fontWeight: 600 }}
    >
      {unHoldEvents.length > 1 ? "Next" : "Checkout"}
    </LoadingButton>
  );
}
